import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { Consignee, ConsigneeInsert } from '../lib/database.types'

export const CONSIGNEES_KEY = ['consignees']

export function useConsignees() {
  return useQuery({
    queryKey: CONSIGNEES_KEY,
    queryFn: async (): Promise<Consignee[]> => {
      const { data, error } = await supabase
        .from('consignees')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useConsignee(id: string | undefined) {
  return useQuery({
    queryKey: [...CONSIGNEES_KEY, id],
    enabled: !!id,
    queryFn: async (): Promise<Consignee> => {
      const { data, error } = await supabase
        .from('consignees')
        .select('*')
        .eq('id', id!)
        .single()
      if (error) throw error
      return data
    },
  })
}

export interface CreateConsigneeInput {
  name: string
  invite_email: string
  cut_pct: number
}

export function useCreateConsignee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateConsigneeInput): Promise<Consignee> => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')
      const row: ConsigneeInsert = {
        owner_user_id: user.id,
        name: input.name,
        invite_email: input.invite_email,
        cut_pct: input.cut_pct,
      }
      const { data, error } = await supabase
        .from('consignees')
        .insert(row)
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONSIGNEES_KEY }),
  })
}

export function useUpdateConsignee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: { id: string; name?: string; cut_pct?: number }): Promise<Consignee> => {
      const { data, error } = await supabase
        .from('consignees')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONSIGNEES_KEY }),
  })
}

export function useRevokeConsignee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase
        .from('consignees')
        .update({ revoked_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONSIGNEES_KEY }),
  })
}

/** Shareable link the owner texts to mom. Lands on /accept-invite which reads the token. */
export function buildInviteLink(token: string): string {
  return `${window.location.origin}/accept-invite?token=${token}`
}
